import { Component, ElementRef, EventEmitter, Output, Renderer2, ViewChild, ViewEncapsulation, OnInit, HostBinding, OnDestroy, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { CarouselModule, CarouselComponent } from 'ngx-carousel-ease';
import { BehaviorSubject, combineLatest, filter, map, mergeMap, Observable, of, Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import { MatSidenav } from '@angular/material/sidenav';
import { Dialog } from '@angular/cdk/dialog';

import { HomeService, State } from '../home/home.service';
import { ProfileService } from '../profile/profile.service';
import { AuthService } from '../auth.service';
import { EncountersService } from './encounters.service';
import { SwipeService } from '../swipe.service';
import { LayoutService } from '../layout.service';
import { LoaderService } from '../loader.service';
import { ChatService } from '../chat/chat.service';

import { GoBackButtonComponent } from '../shared/go-back-button/go-back-button.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { InfoDialogComponent } from '../dialog/info-dialog/info-dialog.component';
import { MatchDialogComponent } from '../dialog/match-dialog/match-dialog.component';

import { User } from '../shared/interfaces/user';

import { ClickAnywhereDirective } from '../click-anywhere.directive';

@Component({
  selector: 'app-new-encounters',
  standalone: true,
  imports: [RouterModule, CommonModule, CarouselModule, CarouselComponent, ClickAnywhereDirective, GoBackButtonComponent, LoaderComponent, InfoDialogComponent],
  templateUrl: './new-encounters.component.html',
  styleUrl: './new-encounters.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NewEncountersComponent implements OnInit, OnDestroy {
  @Output() emitInfoDialogType = new EventEmitter<string>()
  @Output() pageDisplayed = new EventEmitter<string>()

  @ViewChild('suggestionDiv') suggestionDiv!: ElementRef
  @ViewChild('cardSection') cardSection!: ElementRef
  @ViewChild('description') descriptionElt!: HTMLParagraphElement
  @ViewChild('image') image!: ElementRef
  @ViewChild('encounterSidenav') encounterSidenav!: MatSidenav

  @HostBinding('class.newEncounters') addClass: boolean = true // <-- add class to the host to target it in css as encapsulation is off
  loading$ = this.loaderService.loading$;
  user$: Observable<User | null> | undefined
  user!: User
  encounters: User[] = []
  userIndex = 0
  photoIndex = 0
  encounterSidenavToggled = false
  relationshipStatus = new BehaviorSubject('')
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop

  private _swipeCoord?: [number, number];
  private _swipeTime?: number;

  encountersFromService$ = this.homeService.encounters.asObservable()
  notSubscribed = false
  showInfoDialog = false
  infoDialogType = ''
  dialog = inject(Dialog)

  constructor(
    private renderer: Renderer2,
    private homeService: HomeService,
    private location: Location,
    private profileService: ProfileService,
    private authService: AuthService,
    private encountersService: EncountersService,
    private swipeService: SwipeService,
    private layoutService: LayoutService,
    private loaderService: LoaderService,
    private router: Router,
    private chatService: ChatService
  ){}

  ngOnInit(){
    this.user$?.subscribe(res => console.log('user', res))
    this.loaderService.show()
    this.homeService.toggleEncounterSidenav.pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.encounterSidenavToggled = res

      if(this.encounterSidenav) {
        if(this.encounterSidenavToggled){
          this.encounterSidenav.open()
        } else {
          this.encounterSidenav.close()
        }
      }
    })

    this.encountersFromService$.subscribe(res => {
      if(!res.length) {
        this.loaderService.hide()
      }

      this.user$ = this.encountersFromService$.pipe(
        map(res => {
          if(this.chatService.usersContactedByDM.length){
            this.chatService.usersContactedByDM.forEach(id => {
              this.encounters = res.filter(e => e.id !== id)
            })
          } else {
            this.encounters = res
          }

          if(this.encounters.length){
            const user: User = this.encounters[this.userIndex]
            this.profileService.profile.next(user)

            return user
          }
          
          return null
        }),
        filter(Boolean),
        mergeMap((user) =>{
          return combineLatest([of(user), this.encountersService.getRelationshipStatus(user.id)]).pipe(
            map(([user, status]) => {
              this.relationshipStatus.next(status)
  
              if(!user.birthdate) user.birthdate = '0000-00-00'
              
              const [y,m,d] = user.birthdate.split('-')
              const date = `${y}-${m}-${d}`
              const birthDate = dayjs(date)
              const userAge = dayjs().diff(birthDate, 'year')

              this.loaderService.hide()

              return {
                ...user,
                userAge
              }
            }),
          )
        })
      )
    })
  }

  swipe(e: TouchEvent, when: string, user: User): void {
    const coord: [number, number] = [e.changedTouches[0].pageX, e.changedTouches[0].pageY]
    const time = new Date().getTime()

    if (when === 'start') {
      this._swipeCoord = coord
      this._swipeTime = time
    }
    else if (when === 'end') {
      if(!this._swipeCoord || !this._swipeTime) return

      const direction = [coord[0] - this._swipeCoord[0], coord[1] - this._swipeCoord[1]]
      const duration = time - this._swipeTime

      if (duration < 1000
        && Math.abs(direction[0]) > 30 
        && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) { 
        if (direction[0] < 0) {
          //next
          this.swipeService.swipeNext()
          this.rejectProfile(user)
        } else {
          //previous
          this.swipeService.swipePrevious()
          this.likeProfile(user)
        }
      }
    }
  }

  toggleEncounterSidenav(open: boolean){
    this.homeService.toggleEncounterSidenav.next(open)
  }

  getClickNature(clickNature: string, id: number){
    this.encountersService.markProfileAsSeen(id).subscribe()

    if(clickNature === 'anywhere'){
      this.authService.getProfile(id).pipe(
        takeUntil(this._destroyed)
      ).subscribe(res => this.profileService.profile.next(res))
    }
  }

  rejectProfile(user: User){
    // state set to refused on the front end. If relationship does not exist, it stays at refused. Otherwise, if already refused, it is transformed into rejected by the back end
    this.homeService.updateRelationship(user.id, State.refused).pipe(
      map(() => {
        this.renderer.addClass(this.suggestionDiv.nativeElement, 'slide-left')

        setTimeout(() => {
          this.user = this.encounters[this.userIndex]
          this.renderer.addClass(this.suggestionDiv.nativeElement, 'appear')
    
          setTimeout(() => {
            this.renderer.removeClass(this.suggestionDiv.nativeElement, 'appear')
          }, 500)
    
          this.renderer.removeClass(this.suggestionDiv.nativeElement, 'slide-left')
        }, 1500)
      }),
    ).subscribe({
      next: () => {
        setTimeout(() => {
          this.removeProfileFromEncounters(user)
        }, 1500)
      }, 
      error: (err) => {
        if(err.error.error_code === 32){ // "message" : "This user is not subscribed" sent back by the route /relationship/easy
          this.notSubscribed = true
          this.showInfoDialog = true
          this.infoDialogType = 'rejectProfile'
          this.homeService.dialogDisplayed.next(true)
        } else {
          console.error(err)
        }
      }
    })
  }

  likeProfile(user: User){
    console.log('likeProfile ', user.id)
    let state!: State
    if(this.relationshipStatus.getValue() === 'notfriend'){// relationship does not exist
      state = State.accepted
    } else if ( this.relationshipStatus.getValue() === 'pending') {// logged-in user's profile has been liked
      state = State.validated
    }

    this.homeService.updateRelationship(user.id, state).pipe(
      map(() => {
        this.renderer.addClass(this.suggestionDiv.nativeElement, 'slide-right')
        this.renderer.addClass(this.cardSection.nativeElement, 'enhance')
    
        setTimeout(() => {
          this.user = this.encounters[this.userIndex]
          this.renderer.addClass(this.suggestionDiv.nativeElement, 'appear')
    
          setTimeout(() => {
            if(this.suggestionDiv.nativeElement) this.renderer.removeClass(this.suggestionDiv.nativeElement, 'appear')
          }, 500)
    
          this.renderer.removeClass(this.suggestionDiv.nativeElement, 'slide-right')
          this.renderer.removeClass(this.cardSection.nativeElement, 'enhance')
        }, 1500)
      }),
    ).subscribe({
      next: () => {
        this.profileService.setProfileLiked(user)
        setTimeout(() => {
          this.removeProfileFromEncounters(user)
        }, 1500)

        setTimeout(() => {
          this.profileService.setProfileLiked(null)
        }, 3000)

        // if the relationship is validated, show the match dialog
        if(state === 'validated') this.matchDialog(user)
      }, 
      error: (err) => {
        // The user can like up to 5 times without being registered (number of likes checked on back side). If the user is not registed and he reached the maximum number of free likes, we show the dialog. 
        // By clicking on the button, the user is redirected to the landing page.
        if(err.error.error_code === 32){ // "message" : "This user is not subscribed" sent back by the route /relationship/easy
          this.notSubscribed = true
          this.showInfoDialog = true
          this.infoDialogType = 'likeProfile'
          this.homeService.dialogDisplayed.next(true)
        } else {
          console.error(err)
        }
      }
    })
  }

  matchDialog(user: User) {
    this.dialog.open(MatchDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
      data: user
    }).closed.pipe(
      filter(res => !!res)
    ).subscribe(() => {
      if(!user) return

      this.contactProfile(user.id)
    })
  }
  
  closeInfoDialog(e: boolean){
    this.showInfoDialog = !e
    this.infoDialogType = ''
    this.homeService.dialogDisplayed.next(false)
  }

  removeProfileFromEncounters(user: User){
    console.log("removeProfileFromEncounters", user)
    const filteredEncounters = this.encounters.filter(e => e.id !== user.id)
    this.homeService.encounters.next(filteredEncounters)

    if(!filteredEncounters.length) {
      this.homeService.noMoreEncountersAvailable.next(true)
    }
  }

  contactProfile(relatedUserId: number){
    console.log('contactProfile: ', relatedUserId)

    /* 
    Direct messages
    This allows M and F users to send a direct message (feature only available for registered men and all women).

    User A sends a direct message to user B --> Both users are automatically put in contact (autovalidated relationship) 
    + user B receives the messages + when selecting the conversation, user B sees a message asking to accept or refuse the conversation with user A.

    If user B refuses --> We delete the relationship and users are not anymore in contact with one another.
    User B cannot see user A anymore in his contacts + user B cannot see the conversation anymore.
    User A cannot see user B anymore in his contacts BUT he still can see the conversation (and send messages, which user B will never receive).
    If user B accepts --> We keep the relationship and users can keep sending messages to each other.
    */
   
    this.encountersService.getRelationshipStatus(relatedUserId).subscribe(status => {
      // check the user's sex 
      if(this.authService.authMe$.getValue()?.sex === 'man'){
        // the user is a man
        if(this.user?.isSubscribed){
          // if user is registered, then he can send a direct message 

          // check if we already have a conversation with the relatedUser (woman).
          // if so, navigate to /chat/conversation/ + talkId
          // if not, navigate to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
          this.authService.getProfile(relatedUserId).subscribe((user) => {
            if(user.talking_id) {
              this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
              this.chatService.conversationToShow = user.talking_id
            } else {
              this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
              this.chatService.conversationToShow = 0
            }
          })
          
          this.openTalk()
          this.chatService.relatedUserId.next(relatedUserId)
        } else {
          console.log('homme pas abonné')
          if(status === 'friend'){
            // match existing between users
            // check if we already have a conversation with the relatedUser (woman).
            // if so, navigate to /chat/conversation/ + talkId
            // if not, navigate to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
            this.authService.getProfile(relatedUserId).subscribe((user: User) => {
              if(user.talking_id) {
                this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
                this.chatService.conversationToShow = user.talking_id
              } else {
                this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
                this.chatService.conversationToShow = 0
              }
            })
            
            this.openTalk()
            this.chatService.relatedUserId.next(relatedUserId)
          } else {
            // if not, a dialog is opened to redirect the user to the LP
            // if the user is not registered
            // the dialog is displayed. By clicking on the button, the user is redirected to the landing page
            this.showInfoDialog = true
            this.infoDialogType = 'contactProfile'
            this.homeService.dialogDisplayed.next(true)
          }
        }
      } else {
        // the user is a woman
        // she can send a direct message 
        // Check if the user already has a conversation with the male relatedUser.
        // If so, redirect to /chat/conversation/ + talkId
        // If not, redirect to /chat/conversation/new- + relatedUserId. The new conversation creation is done when sending the first message.
        this.authService.getProfile(relatedUserId).subscribe((user) => {
          if(user.talking_id) {
            this.router.navigate(['/home/my-matches/chat/conversation/' + user.talking_id])
            this.chatService.conversationToShow = user.talking_id
          } else {
            this.router.navigate(['/home/my-matches/chat/conversation/new-' + relatedUserId])
            this.chatService.conversationToShow = 0
          }
        })

        this.chatService.relatedUserId.next(relatedUserId)
        this.chatService.usersContactedByDM.push(relatedUserId)
        this.homeService.pageDisplayed.next('my-matches')
        this.chatService.toggleSidenav.next(true)
        this.homeService.dialogDisplayed.next(false)
      }
    })
  }

  openTalk(){
    this.chatService.toggleSidenav.next(true)
    this.homeService.pageDisplayed.next('my-matches')
    this.homeService.dialogDisplayed.next(false)
  }

  closeSidenav() {
    this.homeService.toggleSidenav.next(false)
    this.homeService.pageDisplayed.next('home')
    this.location.back()
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
